<template>
    <BaseContainer>
        <span slot="principal">
            <v-content class="pr-2 pl-2">
                <v-toolbar class="ma-2" style="border-radius: 30px;">
                    <v-btn icon link to="/">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-spacer/>
                    <v-toolbar-title v-show="$vuetify.breakpoint.smAndUp">Upgrades e Downgrades</v-toolbar-title>
                    <v-spacer/>
                    <PeriodoFilter
                        v-model="periodo"
                        defaultPeriodo="Últimos 12 meses"
                        @update="consultar"
                    />
                </v-toolbar>

                <v-alert v-if="!podeConsultar" type="info" text class="ma-1">
                    Você não tem permissão para consultar este relatório.
                    Peça a um administrador a permissão
                    <strong>Zapercontrol Relatorios Upgrades Downgrades Consultar</strong>
                    em Usuários ▸ Permissões.
                </v-alert>

                <v-card v-else elevation="2" class="ma-1">
                    <v-card-text>
                        <v-progress-linear
                            v-if="carregando"
                            indeterminate
                            color="primary"
                            class="mb-2"
                        />

                        <v-row>
                            <v-col cols="12" sm="6" md="4">
                                <Card
                                    :value="formatarValor(totais.upgrades)"
                                    label="Upgrades"
                                    :legenda="qtdTrocas(totais.qtdUpgrades)"
                                    :color="cores.upgrade"
                                    icon="mdi-arrow-up-bold"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="4">
                                <Card
                                    :value="formatarValor(-totais.downgrades)"
                                    label="Downgrades"
                                    :legenda="qtdTrocas(totais.qtdDowngrades)"
                                    :color="cores.downgrade"
                                    icon="mdi-arrow-down-bold"
                                />
                            </v-col>
                            <v-col cols="12" sm="12" md="4">
                                <Card
                                    :value="formatarValor(totais.saldo)"
                                    label="Saldo no MRR"
                                    legenda="Upgrades − downgrades"
                                    :color="+totais.saldo < 0 ? 'red darken-2' : 'green darken-1'"
                                    icon="mdi-scale-balance"
                                />
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Upgrades e downgrades por mês
                                    </div>
                                    <div class="text-center caption text--secondary">
                                        Diferença no valor mensal, no mês em que o cliente paga a primeira fatura
                                        com o valor novo. Plano anual entra como 1/12.
                                    </div>
                                    <v-card-text>
                                        <canvas ref="chartMensal"></canvas>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Trocas mais comuns
                                    </div>
                                    <v-data-table
                                        :headers="headersTrocas"
                                        :items="trocasComuns"
                                        :loading="carregando"
                                        :mobileBreakpoint="0"
                                        :items-per-page="10"
                                        item-key="chave"
                                        disable-sort
                                        dense
                                        style="cursor: pointer;"
                                        @click:row="filtrarTroca"
                                    >
                                        <template v-slot:item.de="{ item }">
                                            <ValorPlano :valor="item.de.valor" :periodicidade="item.de.periodicidade"/>
                                        </template>
                                        <template v-slot:item.para="{ item }">
                                            <ValorPlano :valor="item.para.valor" :periodicidade="item.para.periodicidade"/>
                                        </template>
                                        <template v-slot:item.diferenca="{ item }">
                                            <span :class="corDaDiferenca(item.diferenca)" style="white-space: nowrap;">{{ formatarValor(item.diferenca, true) }}</span>
                                        </template>
                                    </v-data-table>
                                </v-card>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Trocas no período
                                        <v-chip
                                            v-if="mesFiltro"
                                            class="ml-2"
                                            small
                                            close
                                            color="blue-grey"
                                            text-color="white"
                                            @click:close="mesFiltro = null"
                                        >
                                            Mês: {{ mesFiltro }}
                                        </v-chip>
                                        <v-chip
                                            v-if="trocaFiltro"
                                            class="ml-2"
                                            small
                                            close
                                            color="blue-grey"
                                            text-color="white"
                                            @click:close="trocaFiltro = null"
                                        >
                                            {{ descricaoTroca(trocaFiltro) }}
                                        </v-chip>
                                    </div>
                                    <div class="subtitle-2 mb-2" style="text-align: center;">
                                        {{ qtdTrocas(linhasFiltradas.length) }}
                                        | Saldo no MRR: {{ formatarValor(somaDiferencas(linhasFiltradas), true) }}
                                    </div>

                                    <v-row align="center" dense class="px-4">
                                        <v-col cols="12" sm="auto">
                                            <v-btn-toggle v-model="filtroTipo" mandatory dense color="primary">
                                                <v-btn small value="todos">Todos</v-btn>
                                                <v-btn small value="upgrade">Upgrades</v-btn>
                                                <v-btn small value="downgrade">Downgrades</v-btn>
                                            </v-btn-toggle>
                                        </v-col>
                                        <v-col cols="12" sm="6" md="4">
                                            <v-text-field
                                                v-model="busca"
                                                label="Empresa ou ID"
                                                prepend-inner-icon="mdi-magnify"
                                                dense
                                                outlined
                                                hide-details
                                                clearable
                                            />
                                        </v-col>
                                    </v-row>

                                    <v-data-table
                                        :headers="headersLista"
                                        :items="linhasFiltradas"
                                        :loading="carregando"
                                        :mobileBreakpoint="0"
                                        :items-per-page="15"
                                        item-key="cobranca_id"
                                        sort-by="paid_at"
                                        sort-desc
                                        dense
                                    >
                                        <template v-slot:item.paid_at="{ item }">
                                            {{ formatDate(item.paid_at, 'DD/MM/YYYY') }}
                                        </template>
                                        <template v-slot:item.empresa_id="{ item }">
                                            <a @click.stop="$router.push(`/financeiro/${item.empresa_id}`)">
                                                {{ item.empresa_id }}
                                            </a>
                                        </template>
                                        <template v-slot:item.tipo="{ item }">
                                            <v-chip x-small dark :color="cores[item.tipo]">
                                                {{ item.tipo === 'upgrade' ? 'Upgrade' : 'Downgrade' }}
                                            </v-chip>
                                        </template>
                                        <template v-slot:item.de="{ item }">
                                            <ValorPlano :valor="item.valor_cobrado_anterior" :periodicidade="item.periodicidade_anterior"/>
                                        </template>
                                        <template v-slot:item.para="{ item }">
                                            <ValorPlano :valor="item.valor_cobrado" :periodicidade="item.periodicidade"/>
                                        </template>
                                        <template v-slot:item.diferenca="{ item }">
                                            <span :class="corDaDiferenca(item.diferenca)" style="white-space: nowrap;">{{ formatarValor(item.diferenca, true) }}</span>
                                        </template>
                                        <template v-slot:item.motivo="{ item }">
                                            <div style="min-width: 240px; max-width: 360px;">
                                                <div>{{ item.motivo || 'Não registrado' }}</div>
                                                <div
                                                    v-if="item.motivo_cancelamento_id !== 1 && item.cancelamento_descricao"
                                                    class="caption text--secondary"
                                                >
                                                    {{ item.cancelamento_descricao }}
                                                </div>
                                            </div>
                                        </template>
                                    </v-data-table>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-content>
        </span>
    </BaseContainer>
</template>

<script>
import BaseContainer from '@/components/BaseContainer.vue';
import Card from '@/views/home/Card.vue';
import PeriodoFilter from '@/components/PeriodoFilter.vue';
import ValorPlano from './ValorPlano.vue';
import Chart from 'chart.js';

export default {
    name: 'RelatorioUpgradesDowngrades',
    components: { BaseContainer, Card, PeriodoFilter, ValorPlano },

    data: () => ({
        periodo: {},
        carregando: false,
        linhas: [],
        chart: null,

        filtroTipo: 'todos',
        busca: '',
        mesFiltro: null,
        trocaFiltro: null,

        // Mesmas cores de Upgrades e Downgrades no Net MRR
        cores: {
            upgrade: '#42A5F5',
            downgrade: '#FFB74D',
            saldo: '#37474F',
        },

        headersTrocas: [
            { text: 'De', value: 'de' },
            { text: 'Para', value: 'para' },
            { text: 'Trocas', value: 'qtd', align: 'end' },
            { text: 'Diferença no MRR', value: 'diferenca', align: 'end' },
        ],

        headersLista: [
            { text: 'Pago em', value: 'paid_at' },
            { text: 'ID', value: 'empresa_id' },
            { text: 'Empresa', value: 'fantasia' },
            { text: 'Tipo', value: 'tipo' },
            { text: 'De', value: 'de', sortable: false },
            { text: 'Para', value: 'para', sortable: false },
            { text: 'Diferença no MRR', value: 'diferenca', align: 'end' },
            { text: 'Motivo', value: 'motivo' },
        ],
    }),

    computed: {
        podeConsultar() {
            return this.checkPermission('zapercontrol_relatorios_upgrades_downgrades_consultar');
        },

        totais() {
            const upgrades = this.linhas.filter(l => l.tipo === 'upgrade');
            const downgrades = this.linhas.filter(l => l.tipo === 'downgrade');

            return {
                qtdUpgrades: upgrades.length,
                qtdDowngrades: downgrades.length,
                upgrades: this.somaDiferencas(upgrades),
                downgrades: -this.somaDiferencas(downgrades),
                saldo: this.somaDiferencas(this.linhas),
            };
        },

        // Todos os meses do período, inclusive os sem troca
        meses() {
            if (!this.periodo.start || !this.periodo.end) {
                return [];
            }

            const fim = this.moment(this.periodo.end).startOf('month');
            const meses = [];

            for (let m = this.moment(this.periodo.start).startOf('month'); m.isSameOrBefore(fim); m.add(1, 'month')) {
                const chave = m.format('MM/YYYY');
                const doMes = this.linhas.filter(l => this.mesDaLinha(l) === chave);
                const upgrades = doMes.filter(l => l.tipo === 'upgrade');
                const downgrades = doMes.filter(l => l.tipo === 'downgrade');

                meses.push({
                    chave,
                    qtdUpgrades: upgrades.length,
                    qtdDowngrades: downgrades.length,
                    upgrades: this.somaDiferencas(upgrades),
                    downgrades: -this.somaDiferencas(downgrades),
                    saldo: this.somaDiferencas(doMes),
                });
            }

            return meses;
        },

        // Agrupa por valor de → para (com a periodicidade), da troca mais frequente para a menos
        trocasComuns() {
            const grupos = {};

            this.linhas.forEach(l => {
                const chave = this.chaveTroca(l);

                if (!grupos[chave]) {
                    grupos[chave] = {
                        chave,
                        de: { valor: l.valor_cobrado_anterior, periodicidade: l.periodicidade_anterior },
                        para: { valor: l.valor_cobrado, periodicidade: l.periodicidade },
                        qtd: 0,
                        diferenca: 0,
                    };
                }

                grupos[chave].qtd++;
                grupos[chave].diferenca += l.diferenca;
            });

            return Object.values(grupos).sort(
                (a, b) => b.qtd - a.qtd || Math.abs(b.diferenca) - Math.abs(a.diferenca)
            );
        },

        linhasFiltradas() {
            const busca = (this.busca || '').trim().toLowerCase();

            return this.linhas.filter(l =>
                (this.filtroTipo === 'todos' || l.tipo === this.filtroTipo)
                && (!this.mesFiltro || this.mesDaLinha(l) === this.mesFiltro)
                && (!this.trocaFiltro || this.chaveTroca(l) === this.trocaFiltro.chave)
                && (!busca
                    || String(l.empresa_id) === busca
                    || (l.fantasia || '').toLowerCase().includes(busca))
            );
        },
    },

    watch: {
        podeConsultar(pode) {
            if (pode) this.consultar();
        },
    },

    mounted() {
        this.$nextTick(() => this.consultar());
    },

    beforeDestroy() {
        this.chart && this.chart.destroy();
    },

    methods: {
        async consultar() {
            if (!this.podeConsultar || !this.periodo.start) {
                return;
            }

            this.carregando = true;

            try {
                const params = `start=${this.periodo.start}&end=${this.periodo.end}`;
                const { data } = await this.$http.get(`relatorios/upgrades-downgrades?${params}`);

                this.linhas = (data.data || []).map(l => ({ ...l, diferenca: Number(l.diferenca) }));
                this.mesFiltro = null;
                this.trocaFiltro = null;
                this.$nextTick(() => this.gerarGrafico());
            } catch (e) {
                console.error(e);
                this.notify('Erro ao carregar o relatório de upgrades e downgrades', 'warning');
            } finally {
                this.carregando = false;
            }
        },

        mesDaLinha(linha) {
            return this.moment(linha.paid_at).format('MM/YYYY');
        },

        chaveTroca(linha) {
            return [
                linha.periodicidade_anterior,
                linha.valor_cobrado_anterior,
                linha.periodicidade,
                linha.valor_cobrado,
            ].join('|');
        },

        somaDiferencas(linhas) {
            return linhas.reduce((acc, l) => acc + l.diferenca, 0);
        },

        qtdTrocas(qtd) {
            return qtd === 1 ? '1 troca' : `${qtd} trocas`;
        },

        descricaoTroca(troca) {
            const valor = ({ valor, periodicidade }) =>
                `R$ ${this.formatPrice(valor)}${periodicidade === 'anual' ? '/ano' : '/mês'}`;

            return `${valor(troca.de)} → ${valor(troca.para)}`;
        },

        formatarValor(valor, comSinal = false) {
            const n = Number(valor);
            const sinal = n < 0 ? '− ' : (comSinal && n > 0 ? '+ ' : '');

            return `${sinal}R$ ${this.formatPrice(Math.abs(n))}`;
        },

        corDaDiferenca(valor) {
            return valor < 0 ? 'red--text' : 'green--text';
        },

        filtrarTroca(troca) {
            this.trocaFiltro = this.trocaFiltro && this.trocaFiltro.chave === troca.chave ? null : troca;
        },

        filtrarMes(chave) {
            this.mesFiltro = this.mesFiltro === chave ? null : chave;
        },

        gerarGrafico() {
            const ctx = this.$refs.chartMensal;

            if (!ctx) {
                return;
            }

            this.chart && this.chart.destroy();

            const meses = this.meses;
            const qtd = { Upgrades: 'qtdUpgrades', Downgrades: 'qtdDowngrades' };

            this.chart = new Chart(ctx, {
                type: 'bar',
                data: {
                    labels: meses.map(m => m.chave),
                    datasets: [
                        {
                            type: 'line',
                            label: 'Saldo',
                            data: meses.map(m => m.saldo),
                            borderColor: this.cores.saldo,
                            backgroundColor: this.cores.saldo,
                            fill: false,
                            lineTension: 0.3,
                            order: 0,
                        },
                        { label: 'Upgrades', data: meses.map(m => m.upgrades), backgroundColor: this.cores.upgrade, stack: 'mrr', order: 1 },
                        { label: 'Downgrades', data: meses.map(m => -m.downgrades), backgroundColor: this.cores.downgrade, stack: 'mrr', order: 1 },
                    ],
                },
                options: {
                    responsive: true,
                    aspectRatio: this.$vuetify.breakpoint.xsOnly ? 1 : 3.3,
                    hover: { mode: 'index', intersect: false },
                    onHover: (evt, elementos) => {
                        evt.target.style.cursor = elementos.length ? 'pointer' : 'default';
                    },
                    onClick: (evt, elementos) => {
                        if (elementos.length) {
                            this.filtrarMes(meses[elementos[0]._index].chave);
                        }
                    },
                    scales: {
                        xAxes: [{ stacked: true }],
                        yAxes: [{
                            stacked: true,
                            ticks: { callback: v => this.formatarValor(v) },
                        }],
                    },
                    tooltips: {
                        mode: 'index',
                        intersect: false,
                        callbacks: {
                            label: (item, data) => {
                                const label = data.datasets[item.datasetIndex].label;
                                const campo = qtd[label];
                                const vezes = campo ? ` (${this.qtdTrocas(meses[item.index][campo])})` : '';

                                return `${label}: ${this.formatarValor(item.yLabel)}${vezes}`;
                            },
                        },
                    },
                },
            });
        },
    },
}
</script>
